import React, { useEffect } from 'react';
import breakspollLogo from '../assets/images/breakspoll-logo.svg';
import '../assets/css/legal.css';

const LegalPage = () => {
  useEffect(() => {
    document.title = 'Legal | Breakspoll';

    const canonical = document.querySelector('link[rel="canonical"]');
    canonical?.setAttribute('href', 'https://www.breakspoll.com/legal');
  }, []);

  return (
    <main className="legal-page">
      <header className="legal-header">
        <a href="/" aria-label="Return to the Breakspoll home page">
          <img src={breakspollLogo} alt="Breakspoll" className="legal-logo" />
        </a>
        <a href="/" className="legal-back">Back to site</a>
      </header>

      <article className="legal-content">
        <p className="legal-kicker">Legal</p>
        <h1>Privacy &amp; website terms</h1>
        <p className="legal-updated">Last updated: 20th September 2026</p>

        <section>
          <h2>Privacy policy</h2>
          <p>
            Breakspoll is responsible for the personal information collected through this
            website and mailing list. If you have a privacy question or want to exercise
            any of your rights, email <a href="mailto:info@breakspoll.com">info@breakspoll.com</a>.
          </p>
        </section>

        <section>
          <h2>Information we collect</h2>
          <p>
            When you join the mailing list, we collect your name and email address. We
            also identify your country using the country code supplied by Cloudflare for
            your connection; you can change the selected country before submitting the
            form. If you sign up and confirm your subscription, we store your IP address
            with the subscription record so that we can evidence your request, protect
            the list from abuse and investigate delivery or security problems.
          </p>
          <p>
            Cloudflare and our hosting systems may also process routine technical data,
            such as your IP address, browser details, request time and security events,
            to deliver and protect the website.
          </p>
          <p>
            Where Google Analytics is enabled on a Breakspoll service, including our
            merchandise store, it may receive information such as pages viewed, referral
            source, approximate location, device and browser details, interactions,
            online identifiers and purchase or conversion events. We use this limited
            analytics information to understand how the site and store are used and to
            improve their performance.
          </p>
        </section>

        <section>
          <h2>How we use your information</h2>
          <p>
            We use your mailing-list details to send news about Breakspoll events,
            merchandise, voting and related announcements. We rely on your consent for
            these emails. You can withdraw that consent at any time using the unsubscribe
            link in an email or by contacting us.
          </p>
          <p>
            We process limited technical information where necessary for our legitimate
            interests in operating, securing and improving the website. We do not sell
            your personal information.
          </p>
          <p>
            We process store-order information where necessary to provide the products
            and support you request, comply with tax, accounting and consumer-protection
            duties, and pursue our legitimate interests in running and protecting the
            store. Where consent is required for analytics technologies, we rely on that
            consent and you may withdraw it through the available cookie controls.
          </p>
        </section>

        <section>
          <h2>Merchandise store and orders</h2>
          <p>
            The Breakspoll merchandise store is operated using Fourthwall. When you visit
            the store or place an order, Fourthwall processes the checkout, payment,
            production, delivery, returns and customer-service information needed to
            provide the store. Purchases are also subject to Fourthwall's terms and{' '}
            <a href="https://fourthwall.com/legal/privacy-policy" target="_blank" rel="noreferrer">
              privacy policy
            </a>.
          </p>
          <p>
            Fourthwall gives Breakspoll access to information about store orders. This may
            include your name, email address, delivery address, order number, products,
            order value, status and tracking or fulfilment information. We use it to
            manage the store, support customers, understand sales, handle disputes and
            returns, and meet accounting, tax and other legal obligations. Full payment
            card details are handled by Fourthwall and its payment providers and are not
            made available to Breakspoll.
          </p>
          <p>
            You may be offered a separate, optional choice to join the Breakspoll mailing
            list through the Fourthwall store. If you opt in, we use those details as
            described in the mailing-list sections of this policy. Placing an order does
            not itself subscribe you to marketing emails, and you can unsubscribe at any
            time.
          </p>
        </section>

        <section>
          <h2>Services and sharing</h2>
          <p>
            Cloudflare hosts and protects this website. We use a self-hosted Listmonk
            service to manage mailing-list subscriptions, together with the email-delivery
            services configured for that system. We also use Google Analytics where
            enabled, and Fourthwall to operate the merchandise store. These providers
            process information as needed to provide their services and may process it
            outside your country, subject to the safeguards required by applicable law.
            We may also disclose information where required by law or necessary to protect
            our rights, users or services. We do not sell your personal information.
          </p>
        </section>

        <section>
          <h2>How long we keep information</h2>
          <p>
            We keep your mailing-list details until you unsubscribe, withdraw consent or
            ask us to delete them. We may retain a minimal suppression record to respect
            an unsubscribe request and retain consent and IP records where reasonably
            necessary to demonstrate that a subscription was requested or to protect the
            service.
          </p>
          <p>
            Order information is kept for as long as needed to provide customer support,
            manage returns or disputes and meet accounting, tax and other legal duties.
            Analytics information is retained according to our Google Analytics settings
            and only for as long as reasonably needed to measure and improve our services.
            Fourthwall and other providers may retain information under their own legal
            obligations and published policies.
          </p>
        </section>

        <section>
          <h2>Your rights</h2>
          <p>
            Depending on where you live, you may have rights to access, correct, delete,
            restrict or object to the use of your information, to receive a portable copy,
            and to withdraw consent. Contact us at{' '}
            <a href="mailto:info@breakspoll.com">info@breakspoll.com</a>. You may also
            complain to the UK Information Commissioner's Office or your local data
            protection authority.
          </p>
        </section>

        <section>
          <h2>Analytics and cookies</h2>
          <p>
            Breakspoll services use technologies that are necessary for security, network
            delivery, checkout, remembering privacy choices and other functions requested
            by visitors. We also use Google Analytics where it is enabled. Google Analytics
            may use cookies or similar identifiers to measure visits, interactions and
            conversions, but it is not required for you to browse the site, join the mailing
            list or buy merchandise.
          </p>
          <p>
            Where applicable law requires a choice, analytics is used only after permission
            or under an applicable analytics exception with clear information and an easy
            way to object. The Fourthwall store provides its own cookie controls; you can
            use them to accept, reject or later change optional tracking choices. You can
            also restrict cookies through your browser. See{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
              Google's privacy policy
            </a>{' '}
            and{' '}
            <a href="https://fourthwall.com/legal/privacy-policy" target="_blank" rel="noreferrer">
              Fourthwall's privacy policy
            </a>{' '}
            for more information about their processing.
          </p>
        </section>

        <section>
          <h2>Children</h2>
          <p>
            This mailing list is not intended for children under 16, and we do not knowingly
            collect their personal information. Please contact us if you believe a child has
            provided information to us.
          </p>
        </section>

        <section>
          <h2>Website terms</h2>
          <p>
            Breakspoll owns, or uses with permission, the branding, text, photography,
            artwork and other material on this website. You may view it for personal,
            non-commercial use, but may not reproduce, distribute or commercially exploit
            it without permission.
          </p>
          <p>
            We aim to keep the website accurate and available, but do not guarantee that it
            will always be uninterrupted, error-free or current. Links to third-party sites
            are provided for convenience; we are not responsible for their content or
            practices. Nothing in these terms excludes liability that cannot lawfully be
            excluded.
          </p>
        </section>

        <section>
          <h2>Changes to this page</h2>
          <p>
            We may update these terms as the website and our services change. The date at
            the top of this page shows the latest revision.
          </p>
        </section>
      </article>

      <footer className="legal-footer">
        <span>©Breakspoll</span>
        <a href="mailto:info@breakspoll.com">info@breakspoll.com</a>
      </footer>
    </main>
  );
};

export default LegalPage;
